import React from 'react';
import Head from 'next/head';
import { Provider } from 'jotai';
import { Helmet } from '../../components/Helmet';
import { Routes, App, WagWebAppUrl } from '../../constants';
import { getWalkerProfileReviewsV6 } from '../../hooks/api/useGetWalkerProfileReviews';

// Load the path directly instead of barrel file
// https://github.com/vercel/next.js/issues/12557
import { formatter } from '../../utils/formatter/formatter';
import { descriptionParser } from '../../utils/formatter/parsers/walkerProfileHelmetParser/descriptionParser';
import { socialDescriptionParser } from '../../utils/formatter/parsers/walkerProfileHelmetParser/socialDescriptionParser';
import { WalkerProfileV6 } from '../../types/api';
import { WalkerProfileContext } from '../../containers/PCGProfile';
import { getWalkerProfileV6 } from '../../hooks/api/useGetWalkerProfile';
import { getWalkerGalleryV6 } from '../../hooks/api/useGetWalkerGallery';
import { getWalkerLocation } from '../../hooks/api/useGetWalkerLocation';
import { requestHandler } from '../../utils/requestHandler';
import { getWalkerSpecialties } from '../../hooks/api/useGetWalkerSpecialties';
import { getWalkerHomePhotos } from '../../hooks/api/getWalkerHomePhotos';
import { getWalkerServiceAddons } from '../../hooks/api/getWalkerServicesAddons';
import { getWalkerHomeInfo } from '../../hooks/api/getWalkerHomeInfo';
import { getWalkerAchievements } from '../../hooks/api/getWalkerAchievements';
import { getWalkerEndorsements } from '../../hooks/api/useGetWalkerEndorsements';
import { getWalkerAvailability } from '../../hooks/api/getWalkerAvailability';
import { formatAvailabilityDate } from '../../utils/formatAvailabilityDate';
import { ErrorPage } from '../../containers/ErrorPage';
import { getWalkerServicesOffered } from '../../hooks/api/getWalkerOfferedServices';
import { Layout } from '../../components/Layout';
const descriptionFormatter = formatter({
  parser: descriptionParser
});
const socialDescriptionFormatter = formatter({
  parser: socialDescriptionParser
});
const formatBetaTitle = (value: WalkerProfileProps['value'], promoCode: string) => {
  const {
    city,
    administrativeAreaLevel1
  } = value?.location;
  const {
    firstName
  } = value.profile as WalkerProfileV6;
  const title = `Book Services in ${city}, ${administrativeAreaLevel1} with ${firstName} (${promoCode})`;
  return title;
};
export type WalkerProfileProps = Awaited<ReturnType<typeof getWalkerProfileProps>>['props'];
const WalkerProfilePage = ({
  promoCode,
  value,
  queryParams
}: WalkerProfileProps) => {
  if (!value?.profile) {
    return <ErrorPage statusCode={404} message={`Walker profile not found`} />;
  }
  const title = formatBetaTitle(value, promoCode);
  const description = descriptionFormatter(value.profile);
  const socialDescription = socialDescriptionFormatter(value.profile);
  const url = [Routes.WalkerProfileDirectory, promoCode].join('/');
  const image = value?.profile?.photo;
  return <Provider data-sentry-element="Provider" data-sentry-component="WalkerProfilePage" data-sentry-source-file="[promoCode].tsx">
      <Helmet canonical={url} description={description} title={title} robots="noindex, nofollow" openGraph={{
      type: 'article',
      title,
      description: socialDescription,
      siteName: 'Wag!',
      url,
      image
    }} twitter={{
      title,
      description: socialDescription,
      image,
      card: 'summary_large_image',
      creator: App.TwitterUsername,
      site: App.TwitterUsername
    }} data-sentry-element="Helmet" data-sentry-source-file="[promoCode].tsx" />
      <Head data-sentry-element="Head" data-sentry-source-file="[promoCode].tsx">
        <link rel="preconnect" href={WagWebAppUrl.EmbedSignupFlow} />
        <link rel="dns-prefetch" href={WagWebAppUrl.EmbedSignupFlow} />
        <link as="document" rel="preload" href={WagWebAppUrl.EmbedSignupFlow} />
      </Head>
      <Layout data-sentry-element="Layout" data-sentry-source-file="[promoCode].tsx">
        <WalkerProfileContext identifier={promoCode} data={value} queryParams={queryParams} data-sentry-element="WalkerProfileContext" data-sentry-source-file="[promoCode].tsx" />
      </Layout>
    </Provider>;
};
const handleGetServerSideProps = requestHandler();
const getWalkerProfileProps = async ({
  query
}) => {
  const {
    promoCode: promoCodeFromQuery,
    ...restQueryParams
  } = query;
  const promoCode = encodeURI(String(promoCodeFromQuery));

  // Only pass the lat and long to the api/v6/{identifier}/location endpoint if they are numbers
  const coordinatesAreNumbers = typeof restQueryParams?.latitude === 'number' && typeof restQueryParams?.longitude === 'number';
  const [profile, reviews, gallery, location, specialties, serviceAddons, servicesOffered, homePhotos, homeInfo, endorsements, availability] = await Promise.all([getWalkerProfileV6({
    identifier: promoCode
  }), getWalkerProfileReviewsV6({
    identifier: promoCode
  }), getWalkerGalleryV6({
    identifier: promoCode
  }), getWalkerLocation({
    identifier: promoCode,
    ...(coordinatesAreNumbers && {
      queryParams: {
        latitude: restQueryParams?.latitude,
        longitude: restQueryParams?.longitude
      }
    })
  }), getWalkerSpecialties({
    identifier: promoCode
  }), getWalkerServiceAddons({
    identifier: promoCode
  }), getWalkerServicesOffered({
    identifier: promoCode
  }), getWalkerHomePhotos({
    identifier: promoCode
  }), getWalkerHomeInfo({
    identifier: promoCode
  }), getWalkerEndorsements({
    identifier: promoCode
  }), getWalkerAvailability({
    identifier: promoCode,
    queryParams: {
      startDate: formatAvailabilityDate({}),
      endDate: formatAvailabilityDate({
        lastDay: true
      })
    }
  })]);
  let achievements = await getWalkerAchievements({
    identifier: promoCode
  });
  /*
   Since the achievments could be paginated if there are more than 10, the
   initial request will figure out the total and make an extra request if
   that total is greater than the amount on the page. See
   https://wagsters.atlassian.net/browse/CXI-1200?focusedCommentId=129091 for more info
  */

  if (achievements?.perPage < achievements?.total) {
    achievements = await getWalkerAchievements({
      identifier: promoCode,
      queryParams: {
        perPage: achievements.total
      }
    });
  }
  return {
    props: {
      promoCode,
      queryParams: restQueryParams,
      value: {
        profile,
        reviews,
        gallery,
        location,
        specialties,
        serviceAddons,
        servicesOffered,
        homePhotos,
        homeInfo,
        endorsements,
        availability: availability || null,
        achievements
      }
    }
  };
};
export const getServerSideProps = handleGetServerSideProps(getWalkerProfileProps);
export default WalkerProfilePage;